import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectOptions, selectConfig, selectLabels } from '../providers/configSlice';
import * as local from '../components/misc/constants';
import * as localLoan from '../components/misc/loanConstants';

// Surface shared constants from Redux (loan-service /v2/config) with a
// local-constant fallback so the UI never breaks if the fetch hasn't
// completed yet or the gateway is briefly unreachable.
//
// Each key on the returned object mirrors the legacy local-constant name so
// migrating a call site is a one-line swap:
//
//   - import { primaryBankOpts, sectorOpts } from 'components/misc/constants';
//   + const { primaryBankOpts, sectorOpts } = useConfigOptions();
//
// Memoized so consumers can safely destructure values into useEffect deps.
//
// For non-React modules (paymentValues.js, loanPreChecks.js, etc.) use the
// `utils/configAccess.getConfig()` helper instead — it reads from the same Redux
// store without requiring a component context.
export default function useConfigOptions() {
    const options = useSelector(selectOptions);
    const config  = useSelector(selectConfig);
    const labels  = useSelector(selectLabels);

    return useMemo(() => ({
        // ============================================================
        // Pricing & limits  (used by paymentValues.js, eligibleCalc.js)
        // ============================================================
        processingfees:           config?.processingfees           ?? localLoan.processingfees,
        loanInterestRate:         config?.loanInterestRate         ?? localLoan.interestRate,
        loanAmtLimits:            config?.loanAmtLimits            ?? localLoan.loanAmtLimits,
        loanPricing:              config?.loanPricing              ?? localLoan.loanPricing,
        perMonthPaymentFractions: config?.perMonthPaymentFractions ?? localLoan.perMonthPaymentFractions,

        // ============================================================
        // Loan products & lifecycle
        // ============================================================
        loanTypes:    config?.loanTypes    ?? localLoan.loanTypes,
        loanStates:   config?.loanStates   ?? localLoan.loanStates,

        // Loan product catalog from /v2/config, filtered to what clients are
        // allowed to see. Fallback pipes the local picker array through the
        // same filter so cold-start behaves the same.
        loanProducts: (config?.loanProducts ?? local.loanTypes?.map(p => ({ ...p, visibleClient: true })))
                            ?.filter(p => p.visibleClient !== false),

        // ============================================================
        // Form option lists  (Wave 1 — display data)
        // ============================================================
        // pre-existing
        primaryBankOpts:    options?.primaryBanks     ?? local.primaryBankOpts,
        momoProviderOpts:   options?.momoProviders    ?? local.momoProviderOpts,
        sectorOpts:         options?.sectors          ?? local.sectorOpts,
        pensionCompanies:   options?.pensionCompanies ?? local.pensionCompanies,
        insuranceOpts:      options?.insurance        ?? local.insuranceOpts,
        purposeOfLoanOpts:  options?.purposeOfLoan    ?? local.purposeOfLoanOpts,
        billPaymentOptions: options?.billPayment      ?? local.billPaymentOptions,

        // identity / general profile
        optionsYN:           options?.yesNo            ?? local.optionsYN,
        genderOptions:       options?.genderOptions    ?? local.genderOptions,
        nationalityOptions:  options?.nationalities    ?? local.nationalityOptions,
        educationOptions:    options?.educationOptions ?? local.educationOptions,
        occupationOptions:   options?.occupations      ?? local.occupationOptions,
        networkOptions:      options?.networks         ?? local.networkOptions,

        // employment
        numOfYearsWithEmployer: options?.numOfYearsWithEmployer ?? local.numOfYearsWithEmployer,
        employmentLevelOpts:    options?.employmentLevel        ?? local.employmentLevelOpts,
        employmentTypeOpts:     options?.employmentType         ?? local.employmentTypeOpts,
        employmentNatureOpts:   options?.employmentNature       ?? local.employmentNatureOpts,
        // legacy alias — same data, different historical name
        employmentNatureOptions: options?.employmentNature      ?? local.employmentNatureOptions,

        // income / payments
        incomeFrequencyOpts:  options?.incomeFrequency  ?? local.incomeFrequencyOpts,
        automaticPaymentOpts: options?.automaticPayment ?? local.automaticPaymentOpts,
        paymentPlanOpts:      options?.paymentPlan      ?? local.paymentPlanOpts,
        paymentDurationOpts:  options?.paymentDuration  ?? local.paymentDurationOpts,
        missedPaymentOptions: options?.missedPayment    ?? local.missedPaymentOptions,
        billsHistoryOpts:     options?.billsHistory     ?? local.billsHistoryOpts,
        balanceOpts:          options?.balance          ?? local.balanceOpts,

        // pension
        avgPensionContOpts:    options?.avgPensionCont    ?? local.avgPensionContOpts,
        pensionContYrsOpts:    options?.pensionContYrs    ?? local.pensionContYrsOpts,
        pensionContOneYrOpts:  options?.pensionContOneYr  ?? local.pensionContOneYrOpts,
        pensionCollateralOpts: options?.pensionCollateral ?? local.pensionCollateralOpts,
        pensionStateOpts:      options?.pensionState      ?? local.pensionStateOpts,

        // investment / savings / accounts
        collateralOpts:           options?.collateral           ?? local.collateralOpts,
        investmentSizeOpts:       options?.investmentSize       ?? local.investmentSizeOpts,
        investmentFrequencyOpts:  options?.investmentFrequency  ?? local.investmentFrequencyOpts,
        investmentLoanOpts:       options?.investmentLoan       ?? local.investmentLoanOpts,
        savingsSizeOpts:          options?.savingsSize          ?? local.savingsSizeOpts,
        savingsFrequencyOpts:     options?.savingsFrequency     ?? local.savingsFrequencyOpts,
        transactionFrequencyOpts: options?.transactionFrequency ?? local.transactionFrequencyOpts,
        transactionSizeOpts:      options?.transactionSize      ?? local.transactionSizeOpts,
        accountAgeOpts:           options?.accountAge           ?? local.accountAgeOpts,
        accountActiveOpts:        options?.accountActive        ?? local.accountActiveOpts,
        accountBalanceOpts:       options?.accountBalance       ?? local.accountBalanceOpts,
        transFrequencyOpts:       options?.transFrequency       ?? local.transFrequencyOpts,
        consistentOpts:           options?.consistent           ?? local.consistentOpts,
        momoTransactionOpts:      options?.momoTransaction      ?? local.momoTransactionOpts,
        yearsInBusinessOpts:      options?.yearsInBusiness      ?? local.yearsInBusinessOpts,
        plocOpts:                 options?.ploc                 ?? local.plocOpts,
        plocYearsInBusinessOpts:  options?.plocYearsInBusiness  ?? local.plocYearsInBusinessOpts,
        payrollLoanOpts:          options?.payrollLoan          ?? local.payrollLoanOpts,
        salariedOpts:             options?.salaried             ?? local.salariedOpts,

        // business sector / KYC
        customerTypeOpts:     options?.customerType     ?? local.customerTypeOpts,
        otherFinancialOpts:   options?.otherFinancial   ?? local.otherFinancialOpts,
        businessTypeOpts:     options?.businessType     ?? local.businessTypeOpts,
        businessActivityOpts: options?.businessActivity ?? local.businessActivityOpts,
        businessSectorOpts:   options?.businessSector   ?? local.businessSectorOpts,
        demandGoodsOpts:      options?.demandGoods      ?? local.demandGoodsOpts,

        // staff / admin
        relationshipManagers:       config?.relationshipManagers       ?? local.relationshipManagers,
        relationshipManagersOutput: config?.relationshipManagersOutput ?? local.relationshipManagersOutput,

        // ============================================================
        // Display-only label maps  (value → human label)
        // ============================================================
        balanceOptsOutput:             labels?.balance                ?? local.balanceOptsOutput,
        billsHistoryOptsOutput:        labels?.billsHistory           ?? local.billsHistoryOptsOutput,
        missedPaymentOptionsOutput:    labels?.missedPayment          ?? local.missedPaymentOptionsOutput,
        numOfYearsWithEmployerOutput:  labels?.numOfYearsWithEmployer ?? local.numOfYearsWithEmployerOutput,
        sectorOptsOutput:              labels?.sectors                ?? local.sectorOptsOutput,
    }), [options, config, labels]);
}
